package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import kotlin.math.roundToInt

@Composable
fun CollapsingHeaderLayout(
    modifier: Modifier = Modifier,
    header: @Composable () -> Unit,
    pinnedHeader: (@Composable () -> Unit)? = null,
    fab: (@Composable () -> Unit)? = null,
    content: @Composable (contentPadding: PaddingValues) -> Unit,
) {
    val density = LocalDensity.current
    var collapsingHeightPx by remember { mutableStateOf(0) }
    var pinnedHeightPx by remember { mutableStateOf(0) }
    var offsetPx by remember { mutableStateOf(0f) }
    var fabVisible by remember { mutableStateOf(true) }

    val connection = remember {
        object : NestedScrollConnection {
            override fun onPostScroll(
                consumed: Offset,
                available: Offset,
                source: NestedScrollSource,
            ): Offset {
                offsetPx = (offsetPx + consumed.y).coerceIn(-collapsingHeightPx.toFloat(), 0f)
                if (consumed.y < -FAB_SCROLL_THRESHOLD) fabVisible = false
                else if (consumed.y > FAB_SCROLL_THRESHOLD) fabVisible = true
                return Offset.Zero
            }
        }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .nestedScroll(connection)
    ) {
        val topPadding = with(density) { (collapsingHeightPx + pinnedHeightPx).toDp() }
        content(PaddingValues(top = topPadding))

        // Collapsing header — slides fully off the top.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .offset { IntOffset(x = 0, y = offsetPx.roundToInt()) }
                .onSizeChanged { collapsingHeightPx = it.height }
                .background(MaterialTheme.colorScheme.background)
        ) {
            header()
        }

        // Pinned header — rides just below the collapsing header, then stops at the top.
        if (pinnedHeader != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .offset { IntOffset(x = 0, y = (collapsingHeightPx + offsetPx).roundToInt()) }
                    .onSizeChanged { pinnedHeightPx = it.height }
                    .background(MaterialTheme.colorScheme.background)
            ) {
                pinnedHeader()
            }
        }

        // FAB — slides down out of view while scrolling down the list, back up on scroll up.
        if (fab != null) {
            AnimatedVisibility(
                visible = fabVisible,
                modifier = Modifier.align(Alignment.BottomEnd),
                enter = slideInVertically { it } + fadeIn(),
                exit = slideOutVertically { it } + fadeOut(),
            ) {
                fab()
            }
        }
    }
}

private const val FAB_SCROLL_THRESHOLD = 4f
